import { StateReset } from 'ngxs-reset-plugin';

import { Injectable } from '@angular/core';

import { SpinnerService, StateSpinnerService } from '@mitsubishi/ui/spinner';
import { ApmDateUtils } from '@mitsubishi/utils';

import { Dispatch } from '@ngxs-labs/dispatch-decorator';
import { Actions, Store, ofActionSuccessful } from '@ngxs/store';

import { News, NewsRequest, NewsStatuses, NewsTag } from '../../models';
import { NewsAdminActions } from '../actions/news-admin.actions';
import { NewsAdminSelectors } from '../selectors/news-admin.selectors';
import { NewsAdminState } from '../states/news-admin.state';

@Injectable({ providedIn: 'root' })
export class NewsAdminFacade extends StateSpinnerService {
  news$ = this.store.select(NewsAdminSelectors.news);
  activeNews$ = this.store.select(NewsAdminSelectors.activeNews);
  statusesFilter$ = this.store.select(NewsAdminSelectors.statusesFilter);
  tagsNews$ = this.store.select(NewsAdminSelectors.tagsNews);
  statuses$ = this.store.select(NewsAdminSelectors.statuses);
  isNews$ = this.store.select(NewsAdminSelectors.getIsNews);
  tagsFilterNews$ = this.store.select(NewsAdminSelectors.tagsFilterNews);

  get tagsFilterNews(): string[] {
    return this.store.selectSnapshot(NewsAdminSelectors.tagsFilterNews);
  }

  selectedTab$ = this.store.select(NewsAdminSelectors.selectedTab);

  newsCreatedSuccessfully$ = this.actions$.pipe(ofActionSuccessful(NewsAdminActions.CreateNewsAdminSuccess));

  constructor(store: Store, spinnerService: SpinnerService, actions$: Actions) {
    super(store, spinnerService, actions$);

    this.addSpinnerActions([NewsAdminActions.Load]);
    this.addSpinnerActions([NewsAdminActions.CreateNewsAdmin, NewsAdminActions.UpdateNewsAdmin], false);
  }

  @Dispatch()
  loadNewsAdmin() {
    return new NewsAdminActions.Load();
  }

  @Dispatch()
  setNewsAdminType(type: number | null) {
    return new NewsAdminActions.SetNewsAdminType(type);
  }

  @Dispatch()
  setSearchText(searchText: string | null) {
    return new NewsAdminActions.SetSearchText(searchText);
  }

  @Dispatch()
  loadStatusesNews() {
    return new NewsAdminActions.LoadStatuses();
  }

  @Dispatch()
  setStatusesNews(statuses: NewsStatuses[]) {
    return new NewsAdminActions.SetStatuses(statuses);
  }

  @Dispatch()
  setStatusNews(status: string | null) {
    return new NewsAdminActions.SetStatus(status);
  }

  @Dispatch()
  loadTagsNews() {
    return new NewsAdminActions.LoadTags();
  }

  @Dispatch()
  createNewsAdmin(formValues: any) {
    const req = this.getNewsCreateUpdateRequest(formValues);
    const newImage = formValues.creation.image;
    return new NewsAdminActions.CreateNewsAdmin(req, newImage);
  }

  @Dispatch()
  updateNewsAdmin(newsId: string, formValues: any) {
    const req = this.getNewsCreateUpdateRequest(formValues);
    const newImage = formValues.creation.image;
    return new NewsAdminActions.UpdateNewsAdmin(newsId, req, newImage);
  }

  @Dispatch()
  deleteNewsAdmin(news: News) {
    return new NewsAdminActions.DeleteNewsAdmin(news);
  }

  @Dispatch()
  deleteNewsPermanentlyAdmin(id: string) {
    return new NewsAdminActions.DeleteNewsPermanentlyAdmin(id);
  }

  @Dispatch()
  duplicateNewsAdmin(id: string) {
    return new NewsAdminActions.DuplicateNewsAdmin(id);
  }

  @Dispatch()
  resetState() {
    return new StateReset(NewsAdminState);
  }

  @Dispatch()
  setSearchTags(tags: string[]) {
    return new NewsAdminActions.SetSearchTags(tags);
  }

  private getNewsCreateUpdateRequest(formValues: any): NewsRequest {
    const firstStepValues = formValues.creation;
    const secondStepValues = formValues.visibility;
    const thirdStepValues = formValues.tags;
    // const firstStepTags: string[] = firstStepValues.tags.map((t) => ({ context: null, key: t, value: t }));

    const catalogTags: NewsTag[] = [
      // ...firstStepTags.map((t) => ({ context: null, key: t, value: t })),
      ...firstStepValues.tags,
      ...thirdStepValues.classes,
      ...thirdStepValues.subClasses,
      ...thirdStepValues.subFamilies,
      ...thirdStepValues.models,
    ];
    return {
      title: firstStepValues.title,
      content: firstStepValues.content,
      status: firstStepValues.status,
      publishDate: ApmDateUtils.toIsoDateTime(firstStepValues.publishDate),
      expiringDate: ApmDateUtils.toIsoDateTime(firstStepValues.expiringDate),
      isNews: this.store.selectSnapshot(NewsAdminSelectors.getIsNews),
      tags: catalogTags,
      ...secondStepValues,
    };
  }
}
